import React, { Fragment, RefObject, useContext, useEffect, useMemo, useState } from 'react';
import { PostScheduleViewProps } from '../PostSchedule.view';
import { useDimensions } from '../../../../hooks/useDimensions';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { useTopNavClasses } from 'hooks/useTopNavClasees';
import classNames from 'classnames';
import classes from '../PostSchedule.module.scss';
import { PostScheduleViewContext } from '../PostSchedule.controller';
import { BasePostCard } from '../../../../components/cards/BasePostCard';
import { PostScheduleFilterMenu } from '../PostScheduleFilterMenu';
import { CenteredContentLayout } from '../../../../components/containers/CenteredContentLayout';
import { SelectAllCheckboxes } from '../../../../components/actions/SelectAllCheckboxes';
import { Select } from '../../../../components/forms/Select';
import { Loader } from '../../../../components/containers/Loader';
import { DetailedStatsModal } from '../DetailedStatsModal';
import { LibrarySort } from '../../../../constants/post';
import { CreateEditCategoryModal } from '../../Categories/CreateEditCategoryModal';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import {
  PostItemFragment,
  PostStatus,
  QueueCountsFieldsFragment,
  QueueFilterPostStatus,
  QueueSortOrder
} from '../../../../models/graphql';
import { PostScheduleContext } from '../../../../providers/PostScheduleProvider';
import { PostScheduleEmptyHeroBox } from '../PostScheduleEmptyHeroBox';
import { PostScheduleHelpers } from '../postScheduleHelpers';
import { ScrollProvider } from '../../../../providers/ScrollProvider';
import { PostCardViewProps } from '../../../../components/cards/BasePostCard/BasePostCard.view';
import { ListDateHeader } from '../PostScheduleList/PostScheduleList.view';
import { WeekdayDayMonthYearFullFormat } from '../../../../constants/time-formats';
import { useWatch } from 'react-hook-form';
import {
  statisticsCondition,
  statisticsConditionType
} from '../../../../components/cards/helpers/getStatisticsCondition';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';

export type PostScheduleLibraryViewProps = PostScheduleViewProps & {
  leftColRef: RefObject<HTMLDivElement>;
  rightColRef: RefObject<HTMLDivElement>;
  getLibraryActionMenuListItems: (
    post: PostItemFragment,
    statConditions: statisticsConditionType
  ) => ContainerListItem[];
  posts: PostItemFragment[];
  hasReachedFetchLimit: boolean;
  fetchMoreLibraryItems: (scrollDirection: QueueSortOrder, scrollPosition: 'TOP' | 'BOTTOM') => void;
  sortOrderHandler: (value: string) => void;
  queueCounts: QueueCountsFieldsFragment;
};

export const PostScheduleLibraryView = (props: PostScheduleLibraryViewProps): JSX.Element => {
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;
  const { state: topNavOffsetState } = useContext(TopNavContext);
  const { loadingInitial } = useContext(PostScheduleContext);
  const { hasScrollbar: hasLeftScrollbar } = useDimensions(props.leftColRef, 250);
  const [scrollPlacement, setScrollPlacement] = useState<'TOP' | 'BOTTOM' | undefined>(undefined);
  const [hasDateHeader, setHasDateHeader] = useState<boolean>(true);

  const hasSidebar = props.width >= BreakpointsValuesEnum.medium;
  const hasStackedSortBy = props.width < BreakpointsValuesEnum.tabletportrait;

  const topNavClasses = useTopNavClasses(topNavOffsetState);
  const leftColumnWrapperClass = classNames(classes.LeftSideWrapper, 'show-for-medium', topNavClasses.vheight);
  const leftColumnClass = classNames(classes.LeftSideColumn, hasLeftScrollbar ? classes.LeftSideColumnScroller : null);
  const centerColumnClasses = classNames(classes.CentralContent);

  const {
    context,
    orientation,
    posts,
    formInstance,
    getLibraryActionMenuListItems,
    showDetailsModalHandler,
    fetchMoreLibraryItems,
    queueModals
  } = props;

  const sortByValue = useWatch({ name: 'sortBy', control: formInstance.control });

  const postItems = useMemo<JSX.Element[]>((): JSX.Element[] => {
    let currentDateTimeString: string;

    return posts.map((post, index) => {
      let postLibraryCardProps: PostCardViewProps;
      let showDateHeader = false;

      const statConditions = statisticsCondition(post, undefined, undefined, true);
      const dateTime = PostScheduleHelpers.getLocalDateFormatted(
        new Date(post.iterationAggregations?.oldestPost?.dateAdded),
        WeekdayDayMonthYearFullFormat
      );

      if (dateTime !== currentDateTimeString && hasDateHeader) {
        showDateHeader = true;
        currentDateTimeString = dateTime;
      }

      const hideCheckbox = post.status === PostStatus.PUBLISHED || (showOnboarding && disableDelete);
      const postCardProps: PostCardViewProps = {
        index,
        context,
        orientation,
        post,
        openDetailsModal: () => showDetailsModalHandler(post.id),
        menuListItems: getLibraryActionMenuListItems(post, statConditions),
        hideCheckbox: true
      };
      if (hideCheckbox) {
        postLibraryCardProps = { ...postCardProps, hideCheckbox };
      } else {
        postLibraryCardProps = {
          ...postCardProps,
          hideCheckbox,
          useFormInstance: formInstance
        };
      }

      const listDateHeaderContainer = classNames('', {
        [classes.NoListDateHeader]: sortByValue !== 'LATEST' && sortByValue !== 'OLDEST',
        [classes.NoListDateHeaderLibrary]: !hasDateHeader
      });

      return (
        <Fragment key={`POST-${context}-${post.id}-${index}`}>
          {(showDateHeader || (!showDateHeader && index === 0)) && (
            <div className={listDateHeaderContainer}>
              <ListDateHeader
                dateTimeString={dateTime}
                postKey={`LIST-${post.dateAdded}`}
                offsetCheckbox={!hideCheckbox ?? undefined}
                hideDateString={!showDateHeader}
                isFirstDateBlock={index === 0}
              />
            </div>
          )}
          <div className={'margin-bottom-small-16'}>
            <BasePostCard {...postLibraryCardProps} />
          </div>
        </Fragment>
      );
    });
  }, [
    showOnboarding,
    disableDelete,
    posts,
    hasDateHeader,
    context,
    orientation,
    getLibraryActionMenuListItems,
    sortByValue,
    showDetailsModalHandler,
    formInstance
  ]);

  const filterMenuWrapperClasses = classNames({
    'padding-top-small-32': props.context === PostScheduleViewContext.LIBRARY
  });
  const formItemsContainerClasses = classNames(classes.FormItemsContainer, {
    'grid-x': !hasStackedSortBy,
    'grid-y': hasStackedSortBy,
    'padding-top-small-24': props.context === PostScheduleViewContext.LIBRARY && !hasStackedSortBy
  });

  const postItemsWrapperClasses = classNames(classes.PostItemsWrapper, {
    [classes.PostItemsWrapperWithMore]: !props.hasReachedFetchLimit
  });

  const selectAllContainer = classNames(classes.SelectAllContainer, {
    'cell auto': !hasStackedSortBy,
    'small-12 small-order-2': hasStackedSortBy,
    hide: posts.some(p => p.status === PostStatus.PUBLISHED) && hasStackedSortBy,
    [classes.SelectAllContainerStacked]: hasStackedSortBy
  });
  const sortByContainerClasses = classNames(classes.SortByContainer, {
    'cell shrink': !hasStackedSortBy,
    'small-12 small-order-1': hasStackedSortBy
  });

  const sortListItems = Object.keys(LibrarySort).map(key => {
    return { label: LibrarySort[key].label, value: key };
  });

  useEffect(() => {
    setHasDateHeader(sortByValue === 'LATEST' || sortByValue === 'OLDEST');
  }, [sortByValue]);

  const sortByWrapperClasses = classNames(classes.SortByWrapper, {
    [classes.SortByWrapperLibraryNoDateHeader]: !hasDateHeader
  });

  return (
    <>
      {hasSidebar && (
        <div className={leftColumnWrapperClass}>
          <div className={leftColumnClass} ref={props.leftColRef}>
            <div className={filterMenuWrapperClasses}>
              <PostScheduleFilterMenu
                filters={props.filters}
                context={props.context}
                dispatchFilters={props.dispatchFilters}
                categories={props.categories}
                socialProfiles={props.socialProfiles}
                formInstance={props.formInstance}
                onSubmit={props.onSubmit}
                setSearchTerm={props.setSearchTerm}
                queueCounts={props.queueCounts}
                isLoading={loadingInitial}
              />
            </div>
          </div>
        </div>
      )}
      <ScrollProvider
        className={centerColumnClasses}
        scrollToBottomHandler={() => {
          setScrollPlacement('BOTTOM');
          fetchMoreLibraryItems(QueueSortOrder.DESCENDING, 'BOTTOM');
        }}
        scrollToTopHandler={() => {
          setScrollPlacement('TOP');
          fetchMoreLibraryItems(QueueSortOrder.ASCENDING, 'TOP');
        }}>
        <CenteredContentLayout
          containerClasses={classes.ContainerClassCentralizedContent}
          loading={loadingInitial}
          hasSidebar={hasSidebar}>
          <div className={'margin-top-small-8 margin-top-medium-24'}>
            {!loadingInitial && props.loadingMore && (
              <div className={classes.TopScrollZone}>
                {props.loadingMore && scrollPlacement === 'TOP' && (
                  <Loader variation={'dark'} size={'medium'} type={'inline'} />
                )}
              </div>
            )}
            <div className={formItemsContainerClasses} ref={props.rightColRef}>
              <div className={selectAllContainer}>
                {posts.length > 0 &&
                  props.queueFilters.status !== QueueFilterPostStatus.PAST &&
                  !(showOnboarding && disableDelete) && (
                    <SelectAllCheckboxes
                      formFieldName={'postItemCheckBox'}
                      formInstance={formInstance}
                      label={'Select All'}
                      size={'large'}
                    />
                  )}
              </div>
              <div className={sortByContainerClasses}>
                <div className={sortByWrapperClasses}>
                  {Object.keys(postItems).length > 0 && (
                    <Select
                      items={sortListItems}
                      name={'sortBy'}
                      rules={{}}
                      size={'medium'}
                      useFormInstance={formInstance}
                      label={'Sort by'}
                      action={(value, complete) => {
                        props.sortOrderHandler(value as string);
                        complete(true);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={postItemsWrapperClasses}>
              {Object.keys(postItems).length > 0 ? (
                postItems
              ) : (
                <PostScheduleEmptyHeroBox
                  status={PostScheduleHelpers.mapStringToStatusType(props.filters.status)}
                  context={props.context}
                  categoryIdFilter={props.filters.category}
                  socialProfileIdFilter={props.filters.profile}
                />
              )}
            </div>
            {!loadingInitial && props.loadingMore && (
              <div className={classes.BottomScrollZone}>
                {props.loadingMore && scrollPlacement === 'BOTTOM' && (
                  <Loader variation={'dark'} size={'medium'} type={'inline'} />
                )}
              </div>
            )}
          </div>
        </CenteredContentLayout>
      </ScrollProvider>
      {queueModals.details.state && (
        <DetailedStatsModal
          posts={props.postIterations}
          onClose={() => queueModals.details.setter(false)}
          getActionMenuListItems={props.getActionMenuListItems}
          reschedulePostHandler={props.duplicateOrReschedulePost}
        />
      )}
      {props.showCategoryEditModal && (
        <CreateEditCategoryModal
          onClose={() => props.setShowCategoryEditModal(false)}
          category={props.selectedCategory}
        />
      )}
    </>
  );
};
